import React, { useEffect, useMemo, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useActiveClinic } from "../../../context/ClinicContext"
import { useClinicPlanSnapshot } from "../hooks/useClinicPlanSnapshot"
import {
  changeSubscriptionPlan,
  getClinicSubscriptionStatus,
  getSubscriptionPlans,
  previewSubscriptionChange,
} from "../api/subscriptionsApi"
import {
  getFeatureConfig,
  getPlanLabel,
  resolveRequiredPlanFromPlans,
} from "../utils/planPresentation"

const PLAN_THEME = {
  BASIC: {
    glow: "linear-gradient(135deg, #F8FAFC 0%, #EEF2F7 100%)",
    border: "#E2E8F0",
    tagBg: "#F8FAFC",
    tagText: "#334155",
    accent: "#334155",
  },
  PLUS: {
    glow: "linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)",
    border: "#BFDBFE",
    tagBg: "#EFF6FF",
    tagText: "#1D4ED8",
    accent: "#1D4ED8",
  },
  PRO: {
    glow: "linear-gradient(135deg, #F5F3FF 0%, #EDE9FE 100%)",
    border: "#DDD6FE",
    tagBg: "#F5F3FF",
    tagText: "#6D28D9",
    accent: "#6D28D9",
  },
  ENTERPRISE: {
    glow: "linear-gradient(135deg, #ECFEFF 0%, #CFFAFE 100%)",
    border: "#A5F3FC",
    tagBg: "#ECFEFF",
    tagText: "#0F766E",
    accent: "#0F766E",
  },
}

function sortPlans(plans) {
  return [...(plans || [])].sort(
    (a, b) => Number(a.displayOrder || 0) - Number(b.displayOrder || 0),
  )
}

function resolveRequestedFeature(featureKey, plans) {
  if (!featureKey) return null

  const base = getFeatureConfig(featureKey)
  if (!base) return null

  const requiredPlanCode = resolveRequiredPlanFromPlans(featureKey, plans)
  const requiredPlanLabel = getPlanLabel(requiredPlanCode)

  return {
    ...base,
    requiredPlanCode: requiredPlanCode || null,
    requiredPlanLabel: requiredPlanLabel || "Plan superior",
  }
}

function formatMoney(value) {
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    maximumFractionDigits: 2,
  }).format(Number(value || 0))
}

function formatDate(value) {
  if (!value) return "—"

  return new Intl.DateTimeFormat("es-MX", {
    dateStyle: "medium",
  }).format(new Date(value))
}

function formatDateTime(value) {
  if (!value) return "—"

  return new Intl.DateTimeFormat("es-MX", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value))
}

function getStatusLabel(status) {
  switch (status) {
    case "ACTIVE":
      return "Activa"
    case "TRIALING":
      return "En trial"
    case "PAST_DUE":
      return "Con adeudo"
    case "CANCELLED":
      return "Cancelada"
    case "EXPIRED":
      return "Expirada"
    default:
      return status || "—"
  }
}

function getActionLabel(action) {
  switch (action) {
    case "ACTIVATION":
      return "Activación"
    case "REACTIVATION":
      return "Reactivación"
    case "UPGRADE_IMMEDIATE":
      return "Upgrade inmediato"
    case "DOWNGRADE_SCHEDULED":
      return "Downgrade programado"
    case "TRIAL_CHANGE_IMMEDIATE":
      return "Cambio inmediato durante trial"
    case "LATERAL_IMMEDIATE":
      return "Cambio inmediato"
    case "SCHEDULE_CYCLE_CHANGE":
      return "Cambio de ciclo programado"
    case "CLEAR_SCHEDULED_CHANGE":
      return "Cancelar cambio programado"
    case "NO_CHANGE":
      return "Sin cambios"
    case "BLOCKED_PAST_DUE":
      return "Bloqueado"
    default:
      return action || "—"
  }
}

function getModeLabel(mode) {
  switch (mode) {
    case "IMMEDIATE":
      return "Inmediato"
    case "SCHEDULED":
      return "Programado"
    case "BLOCKED":
      return "Bloqueado"
    case "NONE":
      return "Sin acción"
    default:
      return mode || "—"
  }
}

function getBillingCycleLabel(cycle) {
  switch (cycle) {
    case "MONTHLY":
      return "Mensual"
    case "YEARLY":
      return "Anual"
    default:
      return "—"
  }
}

function getPlanFeatureList(plan) {
  return [
    {
      key: "allowExecutiveDashboard",
      label: "Dashboard ejecutivo",
      enabled: Boolean(plan.allowExecutiveDashboard),
    },
    {
      key: "allowAdvancedReports",
      label: "Reportes avanzados",
      enabled: Boolean(plan.allowAdvancedReports),
    },
    {
      key: "allowMultiClinic",
      label: "Multi-clínica",
      enabled: Boolean(plan.allowMultiClinic),
    },
    {
      key: "allowAI",
      label: "IA",
      enabled: Boolean(plan.allowAI),
    },
    {
      key: "allowAudit",
      label: "Auditoría",
      enabled: Boolean(plan.allowAudit),
    },
  ]
}

function getPreviewStory(previewData) {
  if (!previewData) {
    return "Selecciona un plan para ver qué pasará con tu suscripción."
  }

  switch (previewData.action) {
    case "UPGRADE_IMMEDIATE":
      return "El nuevo plan entra hoy. Solo se calcula la diferencia proporcional del tiempo restante."
    case "DOWNGRADE_SCHEDULED":
      return "Tu plan actual se mantiene hasta el final del periodo pagado y el cambio entra automáticamente en el siguiente corte."
    case "SCHEDULE_CYCLE_CHANGE":
      return "No cambia el plan hoy. Solo se programa el cambio de ciclo para la siguiente renovación."
    case "TRIAL_CHANGE_IMMEDIATE":
      return "Estás en trial. El nuevo plan se aplica hoy sin prorrateo."
    case "CLEAR_SCHEDULED_CHANGE":
      return "Se cancelará el cambio pendiente y conservarás el plan actual."
    case "REACTIVATION":
      return "La suscripción se reactiva desde este momento con un nuevo periodo activo."
    case "LATERAL_IMMEDIATE":
      return "El cambio se aplica de inmediato sin ajuste proporcional."
    case "NO_CHANGE":
      return "No hace falta confirmar nada porque ya estás en ese estado."
    case "BLOCKED_PAST_DUE":
      return "Primero debes regularizar la suscripción antes de hacer cambios."
    default:
      return previewData.reason || "Revisa la vista previa antes de confirmar."
  }
}

function getPlanRecurringPrice(plan, billingCycle) {
  if (!plan) return 0

  if (billingCycle === "YEARLY") {
    if (plan.priceYearly != null) return Number(plan.priceYearly || 0)
    return Number(plan.priceMonthly || 0) * 12
  }

  return Number(plan.priceMonthly || 0)
}

function getLimitDelta(currentValue, nextValue) {
  const current =
    currentValue == null ? Number.POSITIVE_INFINITY : Number(currentValue)
  const next =
    nextValue == null ? Number.POSITIVE_INFINITY : Number(nextValue)

  if (current === next) return "same"
  if (next > current) return "up"
  return "down"
}

function getBooleanDelta(currentValue, nextValue) {
  const current = Boolean(currentValue)
  const next = Boolean(nextValue)

  if (current === next) return "same"
  if (!current && next) return "up"
  return "down"
}

function getComparisonRows(currentPlan, selectedPlan) {
  if (!currentPlan || !selectedPlan) return []

  return [
    {
      label: "Usuarios",
      current: currentPlan.maxUsers ?? "∞",
      next: selectedPlan.maxUsers ?? "∞",
      delta: getLimitDelta(currentPlan.maxUsers, selectedPlan.maxUsers),
    },
    {
      label: "Doctores",
      current: currentPlan.maxDoctors ?? "∞",
      next: selectedPlan.maxDoctors ?? "∞",
      delta: getLimitDelta(currentPlan.maxDoctors, selectedPlan.maxDoctors),
    },
    {
      label: "Sillas",
      current: currentPlan.maxChairs ?? "∞",
      next: selectedPlan.maxChairs ?? "∞",
      delta: getLimitDelta(currentPlan.maxChairs, selectedPlan.maxChairs),
    },
    {
      label: "Clínicas",
      current: currentPlan.maxClinics ?? "∞",
      next: selectedPlan.maxClinics ?? "∞",
      delta: getLimitDelta(currentPlan.maxClinics, selectedPlan.maxClinics),
    },
    {
      label: "Dashboard ejecutivo",
      current: currentPlan.allowExecutiveDashboard ? "Sí" : "No",
      next: selectedPlan.allowExecutiveDashboard ? "Sí" : "No",
      delta: getBooleanDelta(
        currentPlan.allowExecutiveDashboard,
        selectedPlan.allowExecutiveDashboard,
      ),
    },
    {
      label: "Reportes avanzados",
      current: currentPlan.allowAdvancedReports ? "Sí" : "No",
      next: selectedPlan.allowAdvancedReports ? "Sí" : "No",
      delta: getBooleanDelta(
        currentPlan.allowAdvancedReports,
        selectedPlan.allowAdvancedReports,
      ),
    },
    {
      label: "Multi-clínica",
      current: currentPlan.allowMultiClinic ? "Sí" : "No",
      next: selectedPlan.allowMultiClinic ? "Sí" : "No",
      delta: getBooleanDelta(
        currentPlan.allowMultiClinic,
        selectedPlan.allowMultiClinic,
      ),
    },
    {
      label: "IA",
      current: currentPlan.allowAI ? "Sí" : "No",
      next: selectedPlan.allowAI ? "Sí" : "No",
      delta: getBooleanDelta(currentPlan.allowAI, selectedPlan.allowAI),
    },
    {
      label: "Auditoría",
      current: currentPlan.allowAudit ? "Sí" : "No",
      next: selectedPlan.allowAudit ? "Sí" : "No",
      delta: getBooleanDelta(currentPlan.allowAudit, selectedPlan.allowAudit),
    },
  ]
}

function summarizeChanges(rows) {
  const gained = rows.filter((row) => row.delta === "up").map((row) => row.label)
  const lost = rows.filter((row) => row.delta === "down").map((row) => row.label)

  return { gained, lost }
}

export default function UpgradePlanPage() {
  const { activeClinicId } = useActiveClinic()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const storedClinicId = window.localStorage.getItem("odontoflow_clinic_id")

  const clinicId =
    activeClinicId && activeClinicId !== "ALL"
      ? activeClinicId
      : storedClinicId && storedClinicId !== "ALL"
        ? storedClinicId
        : null

  const { data: planSnapshot, refresh: refreshPlanSnapshot } =
    useClinicPlanSnapshot(clinicId)

  const usage = planSnapshot?.usage || {
    usersCount: 0,
    doctorsCount: 0,
    chairsCount: 0,
    clinicsCount: 1,
  }

  const [plans, setPlans] = useState([])
  const [statusData, setStatusData] = useState(null)

  const [selectedPlanCode, setSelectedPlanCode] = useState("")
  const [selectedBillingCycle, setSelectedBillingCycle] = useState("MONTHLY")
  const [previewData, setPreviewData] = useState(null)

  const [loading, setLoading] = useState(true)
  const [loadingPreview, setLoadingPreview] = useState(false)
  const [saving, setSaving] = useState(false)

  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  const mode = searchParams.get("mode")
  const featureQuery = searchParams.get("feature")
  const isReactivationMode = mode === "reactivate"

  const requestedFeature = useMemo(
    () => resolveRequestedFeature(featureQuery, plans),
    [featureQuery, plans],
  )

  const currentPlan = statusData?.plan || null
  const subscription = statusData?.subscription || null
  const access = statusData?.access || null

  const currentCode = currentPlan?.code || ""
  const currentPlanLabel = currentPlan?.name || "Sin plan"
  const accountName =
    statusData?.accountName || planSnapshot?.accountName || "Tu cuenta"

  const currentPlanCatalog = useMemo(() => {
    return plans.find((plan) => plan.code === currentCode) || currentPlan || null
  }, [plans, currentCode, currentPlan])

  const selectedPlan = useMemo(() => {
    return plans.find((plan) => plan.code === selectedPlanCode) || null
  }, [plans, selectedPlanCode])

  const scheduledChange = useMemo(() => {
    if (!subscription) return null

    if (
      subscription.nextPlan ||
      subscription.scheduledChangeAt ||
      subscription.nextBillingCycle ||
      subscription.cancelAtPeriodEnd
    ) {
      return {
        nextPlan: subscription.nextPlan || null,
        scheduledChangeAt: subscription.scheduledChangeAt || null,
        scheduledChangeType: subscription.scheduledChangeType || null,
        nextBillingCycle: subscription.nextBillingCycle || null,
        cancelAtPeriodEnd: Boolean(subscription.cancelAtPeriodEnd),
      }
    }

    return null
  }, [subscription])

  const highlightedPlanCode = requestedFeature?.requiredPlanCode || null

  function getPlanCompatibility(plan) {
    const reasons = []

    if (
      plan?.maxUsers != null &&
      Number(usage.usersCount || 0) > Number(plan.maxUsers)
    ) {
      reasons.push(
        `Tu cuenta tiene ${usage.usersCount} usuario(s) y este plan permite ${plan.maxUsers}.`,
      )
    }

    if (
      plan?.maxDoctors != null &&
      Number(usage.doctorsCount || 0) > Number(plan.maxDoctors)
    ) {
      reasons.push(
        `Tu cuenta tiene ${usage.doctorsCount} doctor(es) y este plan permite ${plan.maxDoctors}.`,
      )
    }

    if (
      plan?.maxChairs != null &&
      Number(usage.chairsCount || 0) > Number(plan.maxChairs)
    ) {
      reasons.push(
        `Tu cuenta tiene ${usage.chairsCount} silla(s) y este plan permite ${plan.maxChairs}.`,
      )
    }

    if (
      plan?.maxClinics != null &&
      Number(usage.clinicsCount || 0) > Number(plan.maxClinics)
    ) {
      reasons.push(
        `Tu cuenta tiene ${usage.clinicsCount} clínica(s) y este plan permite ${plan.maxClinics}.`,
      )
    }

    return {
      isCompatible: reasons.length === 0,
      reasons,
    }
  }

  const selectedCompatibility = selectedPlan
    ? getPlanCompatibility(selectedPlan)
    : {
        isCompatible: true,
        reasons: [],
      }

  const currentRecurringPrice = useMemo(() => {
    return getPlanRecurringPrice(
      currentPlanCatalog,
      subscription?.billingCycle || "MONTHLY",
    )
  }, [currentPlanCatalog, subscription?.billingCycle])

  const selectedRecurringPrice = useMemo(() => {
    return getPlanRecurringPrice(selectedPlan, selectedBillingCycle)
  }, [selectedPlan, selectedBillingCycle])

  const recurringDifference = useMemo(() => {
    return selectedRecurringPrice - currentRecurringPrice
  }, [selectedRecurringPrice, currentRecurringPrice])

  const selectedMonthlyReferencePrice = useMemo(() => {
    return Number(selectedPlan?.priceMonthly || 0)
  }, [selectedPlan])

  const selectedYearlyReferencePrice = useMemo(() => {
    if (!selectedPlan) return 0

    if (selectedPlan.priceYearly != null) {
      return Number(selectedPlan.priceYearly || 0)
    }

    return Number(selectedPlan.priceMonthly || 0) * 12
  }, [selectedPlan])

  const annualSavings = useMemo(() => {
    if (selectedBillingCycle !== "YEARLY") return 0

    const savings =
      Number(selectedMonthlyReferencePrice || 0) * 12 -
      Number(selectedYearlyReferencePrice || 0)

    return Math.max(0, savings)
  }, [
    selectedBillingCycle,
    selectedMonthlyReferencePrice,
    selectedYearlyReferencePrice,
  ])

  const effectiveMonthlyEquivalent = useMemo(() => {
    if (selectedBillingCycle !== "YEARLY") return 0
    if (!selectedYearlyReferencePrice) return 0

    return Number(selectedYearlyReferencePrice || 0) / 12
  }, [selectedBillingCycle, selectedYearlyReferencePrice])

  const annualMonthsSaved = useMemo(() => {
    if (selectedBillingCycle !== "YEARLY") return 0
    if (!selectedMonthlyReferencePrice) return 0

    return Number(annualSavings || 0) / Number(selectedMonthlyReferencePrice || 0)
  }, [selectedBillingCycle, annualSavings, selectedMonthlyReferencePrice])

  const [showAnnualSavingsMode, setShowAnnualSavingsMode] = useState(true)

  const shouldHighlightAnnualSavings =
    selectedBillingCycle === "YEARLY" && showAnnualSavingsMode

  const comparisonRows = useMemo(() => {
    return getComparisonRows(currentPlanCatalog, selectedPlan)
  }, [currentPlanCatalog, selectedPlan])

  const comparisonSummary = useMemo(() => {
    return summarizeChanges(comparisonRows)
  }, [comparisonRows])

  async function loadPageData(targetClinicId) {
    if (!targetClinicId) {
      setPlans([])
      setStatusData(null)
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      const [plansResult, statusResult] = await Promise.all([
        getSubscriptionPlans(),
        getClinicSubscriptionStatus(targetClinicId),
      ])

      setPlans(sortPlans(Array.isArray(plansResult) ? plansResult : []))
      setStatusData(statusResult || null)
    } catch (err) {
      setError(err.message || "No se pudo cargar la información de suscripción")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadPageData(clinicId)
  }, [clinicId])

  useEffect(() => {
    if (selectedPlanCode) return

    if (highlightedPlanCode) {
      setSelectedPlanCode(highlightedPlanCode)
      return
    }

    if (currentCode) {
      setSelectedPlanCode(currentCode)
    }
  }, [highlightedPlanCode, currentCode, selectedPlanCode])

  function handleSelectPlan(planCode) {
    setSelectedPlanCode(planCode)
    setPreviewData(null)
    setError(null)
    setSuccess(null)
  }

  async function handlePreview() {
    if (!clinicId) {
      setError("No hay clínica seleccionada")
      return
    }

    if (!selectedPlanCode) {
      setError("Selecciona un plan")
      return
    }

    if (!selectedCompatibility.isCompatible) {
      setError(
        selectedCompatibility.reasons[0] ||
          "Ese plan no soporta tu uso actual",
      )
      return
    }

    try {
      setLoadingPreview(true)
      setError(null)
      setSuccess(null)

      const result = await previewSubscriptionChange({
        clinicId,
        planCode: selectedPlanCode,
        billingCycle: selectedBillingCycle,
      })

      setPreviewData(result)
    } catch (err) {
      setError(err.message || "No se pudo generar la vista previa")
    } finally {
      setLoadingPreview(false)
    }
  }

  async function handleConfirm() {
    if (!previewData) {
      setError("Primero revisa la vista previa del cambio")
      return
    }

    if (!previewData.allowed) {
      setError(previewData.reason || "No es posible aplicar este cambio")
      return
    }

    const confirmText = [
      `Acción: ${getActionLabel(previewData.action)}`,
      `Modo: ${getModeLabel(previewData.mode)}`,
      previewData.reason ? `Detalle: ${previewData.reason}` : "",
      previewData.billing?.immediateCharge > 0
        ? `Cobro estimado hoy: ${formatMoney(previewData.billing.immediateCharge)}`
        : "Hoy no se generará cobro adicional.",
      previewData.effectiveAt
        ? `Fecha efectiva: ${formatDateTime(previewData.effectiveAt)}`
        : "",
      "",
      "¿Deseas continuar?",
    ]
      .filter(Boolean)
      .join("\n")

    const confirmed = window.confirm(confirmText)
    if (!confirmed) return

    try {
      setSaving(true)
      setError(null)
      setSuccess(null)

      const result = await changeSubscriptionPlan({
        clinicId,
        planCode: selectedPlanCode,
        billingCycle: selectedBillingCycle,
      })

      await Promise.all([
        loadPageData(clinicId),
        typeof refreshPlanSnapshot === "function"
          ? refreshPlanSnapshot()
          : Promise.resolve(),
      ])

      window.dispatchEvent(new Event("odontoflow:subscription-updated"))

      setPreviewData(result?.preview || null)

      if (isReactivationMode) {
        setSuccess(
          `Tu cuenta fue reactivada correctamente con el plan ${getPlanLabel(selectedPlanCode)}.`,
        )

        setTimeout(() => {
          navigate("/dashboard")
        }, 1200)
      } else {
        setSuccess(result?.message || "Cambio de plan aplicado correctamente.")
      }
    } catch (err) {
      setError(err.message || "No se pudo aplicar el cambio de plan")
    } finally {
      setSaving(false)
    }
  }

  const disableConfirm =
    !previewData ||
    !previewData.allowed ||
    previewData.action === "NO_CHANGE" ||
    previewData.mode === "BLOCKED"

  return (
    <div
      style={{
        minHeight: "100vh",
        background:
          "radial-gradient(circle at top left, rgba(99,102,241,0.10), transparent 30%), radial-gradient(circle at top right, rgba(14,165,233,0.10), transparent 28%), #F8FAFC",
        padding: 24,
      }}
    >
      <div
        style={{
          maxWidth: 1380,
          margin: "0 auto",
          display: "grid",
          gap: 18,
        }}
      >
        <div
          style={{
            position: "relative",
            overflow: "hidden",
            borderRadius: 30,
            padding: 24,
            background:
              "linear-gradient(135deg, #0B1220 0%, #16213E 42%, #1D4ED8 100%)",
            color: "#FFFFFF",
            boxShadow: "0 24px 60px rgba(15, 23, 42, 0.24)",
          }}
        >
          <div
            style={{
              position: "absolute",
              right: -50,
              top: -50,
              width: 220,
              height: 220,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.08)",
            }}
          />
          <div
            style={{
              position: "absolute",
              left: -40,
              bottom: -60,
              width: 200,
              height: 200,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.06)",
            }}
          />

          <div
            style={{
              position: "relative",
              display: "grid",
              gridTemplateColumns: "minmax(0, 1.2fr) minmax(280px, 0.8fr)",
              gap: 18,
              alignItems: "center",
            }}
          >
            <div>
              <div
                style={{
                  display: "inline-flex",
                  padding: "7px 12px",
                  borderRadius: 999,
                  background: "rgba(255,255,255,0.10)",
                  border: "1px solid rgba(255,255,255,0.16)",
                  fontSize: 11,
                  fontWeight: 900,
                  letterSpacing: 0.6,
                  textTransform: "uppercase",
                }}
              >
                Suscripción y facturación
              </div>

              <div
                style={{
                  fontSize: 34,
                  fontWeight: 900,
                  lineHeight: 1.05,
                  marginTop: 14,
                }}
              >
                {isReactivationMode ? "Reactiva tu operación" : "Elige el plan ideal para tu clínica"}
              </div>

              <div
                style={{
                  marginTop: 10,
                  maxWidth: 760,
                  fontSize: 15,
                  color: "rgba(255,255,255,0.82)",
                  lineHeight: 1.65,
                }}
              >
                Compara tus opciones de forma clara, revisa el impacto antes de
                confirmar y conoce exactamente cuándo entra en vigor cada cambio.
              </div>

              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                  marginTop: 16,
                }}
              >
                <GlassPill>{accountName}</GlassPill>
                <GlassPill>Plan actual: {currentPlanLabel}</GlassPill>
                <GlassPill>Estado: {getStatusLabel(subscription?.status)}</GlassPill>
                {requestedFeature ? (
                  <GlassPill>Buscando: {requestedFeature.title}</GlassPill>
                ) : null}
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 10,
              }}
            >
              <GlassMetric
                label="Renueva"
                value={formatDate(subscription?.currentPeriodEnd)}
              />
              <GlassMetric
                label="Facturación"
                value={getBillingCycleLabel(subscription?.billingCycle)}
              />
              <GlassMetric
                label="Usuarios"
                value={String(usage.usersCount || 0)}
              />
              <GlassMetric
                label="Doctores"
                value={String(usage.doctorsCount || 0)}
              />
            </div>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 14,
          }}
        >
          <SummaryTile
            label="Plan actual"
            value={currentPlanLabel}
            helper={currentCode || "—"}
          />
          <SummaryTile
            label="Estado"
            value={getStatusLabel(subscription?.status)}
            helper={
              access?.isReadOnly ? "Modo solo lectura activo" : "Operación normal"
            }
          />
          <SummaryTile
            label="Próxima renovación"
            value={formatDate(subscription?.currentPeriodEnd)}
            helper={`Ciclo ${getBillingCycleLabel(subscription?.billingCycle)}`}
          />
          <SummaryTile
            label="Próximo cobro estimado"
            value={formatMoney(currentRecurringPrice)}
            helper={`Con tu plan actual ${getBillingCycleLabel(subscription?.billingCycle)}`}
          />
        </div>

        {requestedFeature ? (
          <NoticeCard tone="info">
            <div style={{ fontWeight: 900, marginBottom: 8 }}>
              Quieres desbloquear: {requestedFeature.title}
            </div>
            <div style={{ fontSize: 14, lineHeight: 1.6 }}>
              Esta función está disponible desde el plan{" "}
              <strong>{requestedFeature.requiredPlanLabel}</strong>.
            </div>
            <div
              style={{
                display: "grid",
                gap: 6,
                marginTop: 10,
              }}
            >
              {(requestedFeature.bullets || []).map((item, index) => (
                <div key={index} style={{ fontSize: 13 }}>
                  • {item}
                </div>
              ))}
            </div>
          </NoticeCard>
        ) : null}

        {(isReactivationMode || access?.isReadOnly) && (
          <NoticeCard tone="danger">
            <div style={{ fontWeight: 900, marginBottom: 8 }}>
              Modo restringido
            </div>
            <div style={{ fontSize: 14, lineHeight: 1.6 }}>
              Tu clínica está en modo solo lectura o requiere reactivación.
              Elige un plan para recuperar operación completa.
            </div>
          </NoticeCard>
        )}

        {scheduledChange ? (
          <div
            style={{
              background: "linear-gradient(135deg, #FFF7ED 0%, #FFFBEB 100%)",
              border: "1px solid #FDBA74",
              color: "#9A3412",
              borderRadius: 22,
              padding: 18,
              boxShadow: "0 10px 24px rgba(180, 83, 9, 0.08)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 12,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 900,
                    marginBottom: 8,
                  }}
                >
                  Cambio ya programado
                </div>

                <div style={{ fontSize: 14, lineHeight: 1.7 }}>
                  {scheduledChange.cancelAtPeriodEnd ? (
                    <>
                      Tu suscripción quedará cancelada al final del periodo actual.
                    </>
                  ) : (
                    <>
                      Tu cambio a{" "}
                      <strong>
                        {scheduledChange.nextPlan?.name || "otro plan"}
                      </strong>{" "}
                      está programado para el{" "}
                      <strong>{formatDateTime(scheduledChange.scheduledChangeAt)}</strong>.
                    </>
                  )}
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <Tag>{getActionLabel(scheduledChange.scheduledChangeType)}</Tag>
                <Tag>
                  {getBillingCycleLabel(scheduledChange.nextBillingCycle)}
                </Tag>
              </div>
            </div>
          </div>
        ) : null}

        {error ? (
          <NoticeCard tone="danger">
            <div style={{ fontWeight: 800 }}>{error}</div>
          </NoticeCard>
        ) : null}

        {success ? (
          <NoticeCard tone="success">
            <div style={{ fontWeight: 800 }}>{success}</div>
          </NoticeCard>
        ) : null}

        <div
          style={{
            background: "#FFFFFF",
            border: "1px solid #E5E7EB",
            borderRadius: 26,
            padding: 20,
            boxShadow: "0 16px 40px rgba(15, 23, 42, 0.05)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              flexWrap: "wrap",
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            <div>
              <SectionKicker>Paso 1</SectionKicker>
              <SectionTitle>Elige cómo quieres facturar</SectionTitle>
              <SectionSubtitle>
                Tu plan puede pagarse mensual o anual. El impacto del cambio se
                calcula con base en este ciclo.
              </SectionSubtitle>
            </div>

            <button
              type="button"
              onClick={() => navigate("/subscriptions-admin")}
              style={{
                border: "1px solid #CBD5E1",
                background: "#FFFFFF",
                color: "#0F172A",
                borderRadius: 14,
                padding: "11px 14px",
                fontWeight: 800,
                cursor: "pointer",
              }}
            >
              Ver detalles administrativos
            </button>
          </div>

          <div
            style={{
              display: "grid",
              gap: 14,
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
                alignItems: "stretch",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gap: 6,
                }}
              >
                <CycleButton
                  active={selectedBillingCycle === "MONTHLY"}
                  onClick={() => {
                    setSelectedBillingCycle("MONTHLY")
                    setPreviewData(null)
                  }}
                >
                  Mensual
                </CycleButton>
              </div>

              <div
                style={{
                  display: "grid",
                  gap: 6,
                }}
              >
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    width: "fit-content",
                    padding: "6px 10px",
                    borderRadius: 999,
                    background: "#ECFDF3",
                    border: "1px solid #A6F4C5",
                    color: "#166534",
                    fontSize: 11,
                    fontWeight: 900,
                    letterSpacing: 0.3,
                    textTransform: "uppercase",
                  }}
                >
                  Recomendado para ahorrar
                </div>

                <CycleButton
                  active={selectedBillingCycle === "YEARLY"}
                  onClick={() => {
                    setSelectedBillingCycle("YEARLY")
                    setPreviewData(null)
                  }}
                >
                  Anual
                </CycleButton>
              </div>
            </div>

            {selectedBillingCycle === "YEARLY" ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  flexWrap: "wrap",
                  alignItems: "center",
                  background: "#F8FAFC",
                  border: "1px solid #E2E8F0",
                  borderRadius: 18,
                  padding: "14px 16px",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 900,
                      color: "#0F172A",
                      marginBottom: 4,
                    }}
                  >
                    Modo de presentación anual
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      color: "#64748B",
                      lineHeight: 1.6,
                    }}
                  >
                    Cambia entre una vista enfocada en ahorro o una vista neutral de precio.
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setShowAnnualSavingsMode((prev) => !prev)}
                  style={{
                    border: "1px solid #CBD5E1",
                    background: shouldHighlightAnnualSavings ? "#ECFDF3" : "#FFFFFF",
                    color: shouldHighlightAnnualSavings ? "#166534" : "#0F172A",
                    borderRadius: 999,
                    padding: "10px 14px",
                    fontWeight: 900,
                    cursor: "pointer",
                    boxShadow: shouldHighlightAnnualSavings
                      ? "0 8px 18px rgba(22, 101, 52, 0.10)"
                      : "0 4px 10px rgba(15, 23, 42, 0.04)",
                  }}
                >
                  {shouldHighlightAnnualSavings
                    ? "Ahorro anual activado"
                    : "Mostrar ahorro anual"}
                </button>
              </div>
            ) : null}
          </div>
        </div>

        <div
          style={{
            background: "#FFFFFF",
            border: "1px solid #E5E7EB",
            borderRadius: 26,
            padding: 20,
            boxShadow: "0 16px 40px rgba(15, 23, 42, 0.05)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              flexWrap: "wrap",
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            <div>
              <SectionKicker>Paso 2</SectionKicker>
              <SectionTitle>Compara y selecciona tu plan</SectionTitle>
              <SectionSubtitle>
                El sistema evita que elijas un plan que no soporte tu uso actual.
              </SectionSubtitle>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                gap: 10,
                minWidth: 320,
              }}
            >
              <MiniStat label="Usuarios" value={String(usage.usersCount || 0)} />
              <MiniStat label="Doctores" value={String(usage.doctorsCount || 0)} />
              <MiniStat label="Sillas" value={String(usage.chairsCount || 0)} />
              <MiniStat label="Clínicas" value={String(usage.clinicsCount || 0)} />
            </div>
          </div>

          {loading ? (
            <div
              style={{
                padding: 20,
                borderRadius: 18,
                background: "#F8FAFC",
                border: "1px solid #E2E8F0",
                color: "#475569",
                fontWeight: 700,
              }}
            >
              Cargando planes...
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(290px, 1fr))",
                gap: 16,
              }}
            >
              {plans.map((plan) => {
                const isCurrent = currentCode === plan.code
                const isSelected = selectedPlanCode === plan.code
                const isHighlighted = highlightedPlanCode === plan.code
                const compatibility = getPlanCompatibility(plan)
                const theme = PLAN_THEME[plan.code] || PLAN_THEME.BASIC
                const featureList = getPlanFeatureList(plan)

                const annualSavingsForCard =
                  selectedBillingCycle === "YEARLY"
                    ? Math.max(
                        0,
                        Number(plan.priceMonthly || 0) * 12 -
                          Number(
                            plan.priceYearly != null
                              ? plan.priceYearly
                              : Number(plan.priceMonthly || 0) * 12,
                          ),
                      )
                    : 0

                const annualMonthsSavedForCard =
                  selectedBillingCycle === "YEARLY" && Number(plan.priceMonthly || 0) > 0
                    ? annualSavingsForCard / Number(plan.priceMonthly || 0)
                    : 0

                const roundedAnnualMonthsSavedForCard =
                  Math.round(Number(annualMonthsSavedForCard || 0) * 10) / 10

                return (
                  <button
                    key={plan.id}
                    type="button"
                    onClick={() => handleSelectPlan(plan.code)}
                    style={{
                      position: "relative",
                      overflow: "hidden",
                      borderRadius: 24,
                      border: isSelected
                        ? "2px solid #111827"
                        : isCurrent
                          ? "2px solid #2563EB"
                          : isHighlighted
                            ? "2px solid #7C3AED"
                            : `1px solid ${theme.border}`,
                      background: "#FFFFFF",
                      padding: 18,
                      textAlign: "left",
                      cursor: "pointer",
                      boxShadow: isSelected
                        ? "0 18px 36px rgba(17, 24, 39, 0.10)"
                        : "0 10px 28px rgba(15, 23, 42, 0.06)",
                      transition: "all 0.18s ease",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        background: theme.glow,
                        opacity: 0.55,
                        pointerEvents: "none",
                      }}
                    />

                    <div style={{ position: "relative" }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 10,
                          flexWrap: "wrap",
                          alignItems: "flex-start",
                          marginBottom: 12,
                        }}
                      >
                        <span
                          style={{
                            display: "inline-flex",
                            padding: "7px 12px",
                            borderRadius: 999,
                            background: theme.tagBg,
                            color: theme.tagText,
                            border: `1px solid ${theme.border}`,
                            fontSize: 11,
                            fontWeight: 900,
                          }}
                        >
                          {getPlanLabel(plan.code)}
                        </span>

                        <div
                          style={{
                            display: "flex",
                            gap: 6,
                            flexWrap: "wrap",
                          }}
                        >
                          {isCurrent ? <Badge color="#2563EB" text="Plan actual" /> : null}
                          {isHighlighted && !isCurrent ? (
                            <Badge color="#7C3AED" text="Recomendado" />
                          ) : null}
                          {isSelected ? (
                            <Badge
                              color={shouldHighlightAnnualSavings ? "#166534" : "#111827"}
                              text={
                                shouldHighlightAnnualSavings
                                  ? "Seleccionado · Ahorro anual"
                                  : "Seleccionado"
                              }
                            />
                          ) : null}
                        </div>
                      </div>

                      <div
                        style={{
                          fontSize: 24,
                          fontWeight: 900,
                          color: "#0F172A",
                          marginBottom: 6,
                        }}
                      >
                        {plan.name}
                      </div>

                      <div
                        style={{
                          minHeight: 44,
                          fontSize: 14,
                          color: "#475569",
                          lineHeight: 1.6,
                          marginBottom: 16,
                        }}
                      >
                        {plan.description ||
                          "Plan listo para operar tu clínica con este nivel de capacidad."}
                      </div>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-end",
                          gap: 6,
                          marginBottom: 10,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 34,
                            fontWeight: 900,
                            color: "#111827",
                            lineHeight: 1,
                          }}
                        >
                          {selectedBillingCycle === "YEARLY"
                            ? formatMoney(
                                plan.priceYearly != null
                                  ? plan.priceYearly
                                  : Number(plan.priceMonthly || 0) * 12,
                              )
                            : formatMoney(plan.priceMonthly)}
                        </div>
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: "#64748B",
                            paddingBottom: 4,
                          }}
                        >
                          / {selectedBillingCycle === "YEARLY" ? "año" : "mes"}
                        </div>
                      </div>

                      {shouldHighlightAnnualSavings && annualSavingsForCard > 0 ? (
                        <div
                          style={{
                            display: "grid",
                            gap: 8,
                            marginBottom: 16,
                          }}
                        >
                          <div
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              width: "fit-content",
                              padding: "8px 12px",
                              borderRadius: 999,
                              background: "#ECFDF3",
                              border: "1px solid #A6F4C5",
                              color: "#166534",
                              fontSize: 12,
                              fontWeight: 900,
                              boxShadow: "0 6px 16px rgba(22, 101, 52, 0.08)",
                            }}
                          >
                            Ahorra {formatMoney(annualSavingsForCard)} al año
                          </div>

                          <div
                            style={{
                              fontSize: 13,
                              color: "#166534",
                              lineHeight: 1.6,
                              fontWeight: 700,
                            }}
                          >
                            Equivale a {roundedAnnualMonthsSavedForCard} mes(es) pagados sin costo adicional.
                          </div>
                        </div>
                      ) : (
                        <div style={{ marginBottom: 16 }} />
                      )}

                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                          gap: 10,
                          marginBottom: 16,
                        }}
                      >
                        <LimitCard label="Usuarios" value={plan.maxUsers ?? "∞"} />
                        <LimitCard label="Doctores" value={plan.maxDoctors ?? "∞"} />
                        <LimitCard label="Sillas" value={plan.maxChairs ?? "∞"} />
                        <LimitCard label="Clínicas" value={plan.maxClinics ?? "∞"} />
                      </div>

                      <div
                        style={{
                          background: "rgba(255,255,255,0.70)",
                          backdropFilter: "blur(6px)",
                          border: "1px solid #E2E8F0",
                          borderRadius: 18,
                          padding: 14,
                          marginBottom: 14,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 900,
                            color: "#0F172A",
                            marginBottom: 10,
                            textTransform: "uppercase",
                            letterSpacing: 0.4,
                          }}
                        >
                          Incluye
                        </div>

                        <div
                          style={{
                            display: "grid",
                            gap: 8,
                          }}
                        >
                          {featureList.map((feature) => (
                            <FeatureRow
                              key={feature.label}
                              label={feature.label}
                              enabled={feature.enabled}
                            />
                          ))}
                        </div>
                      </div>

                      {!compatibility.isCompatible ? (
                        <div
                          style={{
                            background: "#FFF7ED",
                            border: "1px solid #FDBA74",
                            color: "#9A3412",
                            borderRadius: 16,
                            padding: 12,
                          }}
                        >
                          <div style={{ fontWeight: 900, marginBottom: 6 }}>
                            Este plan no soporta tu uso actual
                          </div>
                          <div style={{ display: "grid", gap: 6, fontSize: 13 }}>
                            {compatibility.reasons.map((reason, index) => (
                              <div key={index}>• {reason}</div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div
                          style={{
                            display: "inline-flex",
                            padding: "8px 12px",
                            borderRadius: 999,
                            background: "#F0FDF4",
                            border: "1px solid #BBF7D0",
                            color: "#166534",
                            fontSize: 12,
                            fontWeight: 800,
                          }}
                        >
                          Compatible con tu uso actual
                        </div>
                      )}
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {selectedPlan && currentPlanCatalog ? (
          <div
            style={{
              background: "#FFFFFF",
              border: "1px solid #E5E7EB",
              borderRadius: 26,
              padding: 20,
              boxShadow: "0 16px 40px rgba(15, 23, 42, 0.05)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 12,
                flexWrap: "wrap",
                alignItems: "center",
                marginBottom: 16,
              }}
            >
              <div>
                <SectionKicker>Paso 3</SectionKicker>
                <SectionTitle>Compara tu plan actual vs el seleccionado</SectionTitle>
                <SectionSubtitle>
                  Visualiza lo que mejora, lo que se mantiene y lo que podrías perder.
                </SectionSubtitle>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <Tag>Actual: {currentPlanCatalog.name || "—"}</Tag>
                <Tag>Seleccionado: {selectedPlan.name}</Tag>
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) minmax(300px, 0.9fr)",
                gap: 16,
              }}
            >
              <div
                style={{
                  borderRadius: 20,
                  border: "1px solid #E2E8F0",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1.2fr 0.9fr 0.9fr 110px",
                    background: "#F8FAFC",
                    borderBottom: "1px solid #E2E8F0",
                    fontWeight: 900,
                    color: "#334155",
                    fontSize: 12,
                  }}
                >
                  <div style={{ padding: 12 }}>Característica</div>
                  <div style={{ padding: 12 }}>Actual</div>
                  <div style={{ padding: 12 }}>Seleccionado</div>
                  <div style={{ padding: 12 }}>Cambio</div>
                </div>

                {comparisonRows.map((row) => (
                  <ComparisonRow key={row.label} row={row} />
                ))}
              </div>

              <div
                style={{
                  display: "grid",
                  gap: 14,
                  alignContent: "start",
                }}
              >
                <PriceImpactCard
                  title="Impacto económico"
                  currentLabel="Con tu plan actual"
                  currentValue={formatMoney(currentRecurringPrice)}
                  nextLabel="Si cambias a este plan"
                  nextValue={formatMoney(selectedRecurringPrice)}
                  deltaValue={recurringDifference}
                  cycleLabel={getBillingCycleLabel(selectedBillingCycle)}
                  selectedBillingCycle={selectedBillingCycle}
                  annualSavings={annualSavings}
                  effectiveMonthlyEquivalent={effectiveMonthlyEquivalent}
                  annualMonthsSaved={annualMonthsSaved}
                  emphasizeSavings={shouldHighlightAnnualSavings}
                />

                <ChangeSummaryCard
                  gained={comparisonSummary.gained}
                  lost={comparisonSummary.lost}
                />
              </div>
            </div>
          </div>
        ) : null}

        <div
          style={{
            background: "#FFFFFF",
            border: "1px solid #E5E7EB",
            borderRadius: 26,
            padding: 20,
            boxShadow: "0 16px 40px rgba(15, 23, 42, 0.05)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              flexWrap: "wrap",
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            <div>
              <SectionKicker>Paso 4</SectionKicker>
              <SectionTitle>Revisa el impacto antes de confirmar</SectionTitle>
              <SectionSubtitle>
                Aquí ves si el cambio entra hoy, cuánto se cobraría y si quedará programado.
              </SectionSubtitle>
            </div>

            <button
              type="button"
              onClick={handlePreview}
              disabled={
                !clinicId ||
                !selectedPlanCode ||
                loadingPreview ||
                saving ||
                !selectedCompatibility.isCompatible
              }
              style={{
                border: "none",
                background: "#111827",
                color: "#FFFFFF",
                borderRadius: 16,
                padding: "13px 18px",
                fontWeight: 900,
                cursor:
                  !clinicId ||
                  !selectedPlanCode ||
                  loadingPreview ||
                  saving ||
                  !selectedCompatibility.isCompatible
                    ? "not-allowed"
                    : "pointer",
                opacity:
                  !clinicId ||
                  !selectedPlanCode ||
                  loadingPreview ||
                  saving ||
                  !selectedCompatibility.isCompatible
                    ? 0.6
                    : 1,
              }}
            >
              {loadingPreview ? "Calculando..." : "Ver impacto del cambio"}
            </button>
          </div>

          {!selectedPlan ? (
            <EmptyState>
              Selecciona uno de los planes de arriba para generar la vista previa.
            </EmptyState>
          ) : !previewData ? (
            <EmptyState>
              <div style={{ fontWeight: 800, marginBottom: 8 }}>
                Plan seleccionado: {selectedPlan.name}
              </div>
              <div style={{ lineHeight: 1.7 }}>
                Pulsa <strong>“Ver impacto del cambio”</strong> para saber si el
                ajuste será inmediato o si quedará programado al siguiente corte.
              </div>
            </EmptyState>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1.1fr) minmax(340px, 0.9fr)",
                gap: 16,
              }}
            >
              <div
                style={{
                  borderRadius: 22,
                  background: "#F8FAFC",
                  border: "1px solid #E2E8F0",
                  padding: 18,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    flexWrap: "wrap",
                    marginBottom: 14,
                  }}
                >
                  <Tag>{getActionLabel(previewData.action)}</Tag>
                  <Tag>{getModeLabel(previewData.mode)}</Tag>
                </div>

                <div
                  style={{
                    background:
                      previewData.mode === "BLOCKED"
                        ? "#FEF3F2"
                        : previewData.mode === "SCHEDULED"
                          ? "#FFFBEB"
                          : "#ECFDF3",
                    border:
                      previewData.mode === "BLOCKED"
                        ? "1px solid #FECDCA"
                        : previewData.mode === "SCHEDULED"
                          ? "1px solid #FDE68A"
                          : "1px solid #A7F3D0",
                    borderRadius: 18,
                    padding: 16,
                    marginBottom: 16,
                  }}
                >
                  <div
                    style={{
                      fontSize: 13,
                      color: "#475569",
                      marginBottom: 6,
                    }}
                  >
                    Resumen
                  </div>
                  <div
                    style={{
                      fontSize: 15,
                      fontWeight: 800,
                      color: "#0F172A",
                      lineHeight: 1.65,
                    }}
                  >
                    {previewData.reason || "—"}
                  </div>
                </div>

                <PreviewLine
                  label="Plan actual"
                  value={`${previewData.currentPlan?.name || "—"} ${
                    previewData.currentPlan?.code
                      ? `(${previewData.currentPlan.code})`
                      : ""
                  }`}
                />
                <PreviewLine
                  label="Plan seleccionado"
                  value={`${previewData.targetPlan?.name || "—"} ${
                    previewData.targetPlan?.code
                      ? `(${previewData.targetPlan.code})`
                      : ""
                  }`}
                />
                <PreviewLine
                  label="Ciclo actual"
                  value={getBillingCycleLabel(previewData.billing?.currentCycle)}
                />
                <PreviewLine
                  label="Ciclo solicitado"
                  value={getBillingCycleLabel(previewData.billing?.requestedCycle)}
                />
                <PreviewLine
                  label="Precio actual"
                  value={formatMoney(previewData.billing?.currentPrice)}
                />
                <PreviewLine
                  label="Precio objetivo"
                  value={formatMoney(previewData.billing?.targetPrice)}
                />
                <PreviewLine
                  label="Prorrateo"
                  value={formatMoney(previewData.billing?.prorationAmount)}
                />
                <PreviewLine
                  label="Cobro hoy"
                  value={formatMoney(previewData.billing?.immediateCharge)}
                  highlight={Number(previewData.billing?.immediateCharge || 0) > 0}
                />
                <PreviewLine
                  label="Fecha efectiva"
                  value={formatDateTime(previewData.effectiveAt)}
                />
                <PreviewLine
                  label="Fin del periodo actual"
                  value={formatDateTime(previewData.currentPeriodEnd)}
                />

                {previewData.pendingChange ? (
                  <div
                    style={{
                      marginTop: 16,
                      background: "#FFF7ED",
                      border: "1px solid #FDBA74",
                      color: "#9A3412",
                      borderRadius: 16,
                      padding: 14,
                    }}
                  >
                    <div style={{ fontWeight: 900, marginBottom: 6 }}>
                      Cambio pendiente
                    </div>
                    <div style={{ fontSize: 13, lineHeight: 1.7 }}>
                      El sistema guardará un cambio para aplicarse en el siguiente corte.
                    </div>
                  </div>
                ) : null}
              </div>

              <div
                style={{
                  borderRadius: 22,
                  background: "linear-gradient(180deg, #FFFFFF 0%, #FAFBFF 100%)",
                  border: "1px solid #E5E7EB",
                  padding: 18,
                  display: "grid",
                  gap: 16,
                  alignContent: "start",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 18,
                      fontWeight: 900,
                      color: "#0F172A",
                      marginBottom: 8,
                    }}
                  >
                    Qué pasará al confirmar
                  </div>

                  <div
                    style={{
                      fontSize: 14,
                      color: "#475569",
                      lineHeight: 1.8,
                    }}
                  >
                    {getPreviewStory(previewData)}
                  </div>
                </div>

                <div
                  style={{
                    background: "#F8FAFC",
                    border: "1px solid #E2E8F0",
                    borderRadius: 18,
                    padding: 16,
                  }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 900,
                      color: "#475569",
                      marginBottom: 10,
                      textTransform: "uppercase",
                      letterSpacing: 0.4,
                    }}
                  >
                    Resumen rápido
                  </div>

                  <PreviewPillRow
                    label="Acción"
                    value={getActionLabel(previewData.action)}
                  />
                  <PreviewPillRow
                    label="Modo"
                    value={getModeLabel(previewData.mode)}
                  />
                  <PreviewPillRow
                    label="Hoy pagas"
                    value={formatMoney(previewData.billing?.immediateCharge)}
                  />
                  <PreviewPillRow
                    label="Entra"
                    value={formatDate(previewData.effectiveAt)}
                  />
                </div>

                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={disableConfirm || saving}
                  style={{
                    border: "none",
                    background: disableConfirm
                      ? "#CBD5E1"
                      : "linear-gradient(135deg, #111827 0%, #1D4ED8 100%)",
                    color: "#FFFFFF",
                    borderRadius: 16,
                    padding: "15px 18px",
                    fontWeight: 900,
                    cursor: disableConfirm || saving ? "not-allowed" : "pointer",
                    boxShadow: disableConfirm
                      ? "none"
                      : "0 14px 28px rgba(29, 78, 216, 0.18)",
                  }}
                >
                  {saving
                    ? "Aplicando..."
                    : previewData.action === "CLEAR_SCHEDULED_CHANGE"
                      ? "Cancelar cambio programado"
                      : isReactivationMode
                        ? "Confirmar y reactivar"
                        : shouldHighlightAnnualSavings
                          ? "Confirmar y ahorrar"
                          : "Confirmar cambio"}
                </button>

                <div
                  style={{
                    fontSize: 13,
                    color: "#64748B",
                    lineHeight: 1.7,
                  }}
                >
                  {disableConfirm
                    ? previewData.mode === "BLOCKED"
                      ? "No se puede confirmar mientras la suscripción esté bloqueada."
                      : previewData.action === "NO_CHANGE"
                        ? "No hay cambios nuevos que aplicar."
                        : "Primero revisa una vista previa válida."
                    : "Todo listo. Al confirmar, el sistema aplicará el cambio según la política de tu ciclo actual."}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function GlassPill({ children }) {
  return (
    <span
      style={{
        padding: "7px 11px",
        borderRadius: 999,
        background: "rgba(255,255,255,0.10)",
        border: "1px solid rgba(255,255,255,0.16)",
        color: "#FFFFFF",
        fontSize: 11,
        fontWeight: 800,
      }}
    >
      {children}
    </span>
  )
}

function GlassMetric({ label, value }) {
  return (
    <div
      style={{
        padding: 14,
        borderRadius: 18,
        background: "rgba(255,255,255,0.10)",
        border: "1px solid rgba(255,255,255,0.14)",
        backdropFilter: "blur(8px)",
      }}
    >
      <div
        style={{
          fontSize: 11,
          opacity: 0.8,
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 18,
          fontWeight: 900,
          lineHeight: 1.15,
        }}
      >
        {value}
      </div>
    </div>
  )
}

function SummaryTile({ label, value, helper }) {
  return (
    <div
      style={{
        background: "#FFFFFF",
        border: "1px solid #E5E7EB",
        borderRadius: 22,
        padding: 18,
        boxShadow: "0 12px 28px rgba(15, 23, 42, 0.04)",
      }}
    >
      <div
        style={{
          fontSize: 12,
          color: "#64748B",
          marginBottom: 8,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 24,
          fontWeight: 900,
          color: "#0F172A",
          lineHeight: 1.1,
        }}
      >
        {value}
      </div>
      <div
        style={{
          marginTop: 8,
          fontSize: 12,
          color: "#94A3B8",
        }}
      >
        {helper}
      </div>
    </div>
  )
}

function NoticeCard({ children, tone = "info" }) {
  const styles = {
    info: {
      background: "#EEF2FF",
      border: "#C7D2FE",
      color: "#312E81",
    },
    danger: {
      background: "#FFF1F0",
      border: "#FFA39E",
      color: "#A8071A",
    },
    success: {
      background: "#F6FFED",
      border: "#B7EB8F",
      color: "#237804",
    },
  }

  const style = styles[tone] || styles.info

  return (
    <div
      style={{
        background: style.background,
        border: `1px solid ${style.border}`,
        color: style.color,
        borderRadius: 20,
        padding: 16,
      }}
    >
      {children}
    </div>
  )
}

function SectionKicker({ children }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 900,
        color: "#6366F1",
        letterSpacing: 0.5,
        textTransform: "uppercase",
        marginBottom: 6,
      }}
    >
      {children}
    </div>
  )
}

function SectionTitle({ children }) {
  return (
    <div
      style={{
        fontSize: 22,
        fontWeight: 900,
        color: "#0F172A",
      }}
    >
      {children}
    </div>
  )
}

function SectionSubtitle({ children }) {
  return (
    <div
      style={{
        marginTop: 4,
        fontSize: 13,
        color: "#64748B",
        lineHeight: 1.6,
      }}
    >
      {children}
    </div>
  )
}

function CycleButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        border: active ? "1px solid transparent" : "1px solid #E2E8F0",
        padding: "12px 22px",
        borderRadius: 14,
        background: active
          ? "linear-gradient(135deg, #111827 0%, #1D4ED8 100%)"
          : "#FFFFFF",
        color: active ? "#FFFFFF" : "#0F172A",
        fontWeight: 900,
        cursor: "pointer",
        boxShadow: active
          ? "0 10px 22px rgba(29, 78, 216, 0.18)"
          : "0 4px 12px rgba(15, 23, 42, 0.04)",
        minWidth: 130,
        transition: "all 0.18s ease",
      }}
    >
      {children}
    </button>
  )
}

function MiniStat({ label, value }) {
  return (
    <div
      style={{
        background: "#F8FAFC",
        border: "1px solid #E2E8F0",
        borderRadius: 14,
        padding: 12,
      }}
    >
      <div style={{ fontSize: 11, color: "#64748B", marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 18, fontWeight: 900, color: "#0F172A" }}>
        {value}
      </div>
    </div>
  )
}

function Badge({ color, text }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "6px 10px",
        borderRadius: 999,
        background: color,
        color: "#FFFFFF",
        fontSize: 10,
        fontWeight: 900,
        letterSpacing: 0.4,
        textTransform: "uppercase",
      }}
    >
      {text}
    </span>
  )
}

function LimitCard({ label, value }) {
  return (
    <div
      style={{
        borderRadius: 14,
        background: "rgba(255,255,255,0.74)",
        border: "1px solid #E2E8F0",
        padding: 11,
      }}
    >
      <div style={{ fontSize: 11, color: "#64748B", marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontWeight: 900, color: "#0F172A", fontSize: 16 }}>
        {value}
      </div>
    </div>
  )
}

function FeatureRow({ label, enabled }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 10,
        alignItems: "center",
      }}
    >
      <span style={{ fontSize: 13, color: "#334155" }}>{label}</span>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          minHeight: 24,
          padding: "0 8px",
          borderRadius: 999,
          background: enabled ? "#F0FDF4" : "#F8FAFC",
          border: enabled ? "1px solid #BBF7D0" : "1px solid #E2E8F0",
          color: enabled ? "#166534" : "#64748B",
          fontSize: 11,
          fontWeight: 800,
        }}
      >
        {enabled ? "Sí" : "No"}
      </span>
    </div>
  )
}

function EmptyState({ children }) {
  return (
    <div
      style={{
        padding: 20,
        borderRadius: 20,
        background: "#F8FAFC",
        border: "1px dashed #CBD5E1",
        color: "#475569",
      }}
    >
      {children}
    </div>
  )
}

function Tag({ children }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "7px 10px",
        borderRadius: 999,
        background: "#F8FAFC",
        border: "1px solid #E2E8F0",
        color: "#334155",
        fontSize: 12,
        fontWeight: 800,
      }}
    >
      {children}
    </span>
  )
}

function PreviewLine({ label, value, highlight = false }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
        padding: "11px 0",
        borderBottom: "1px solid #E2E8F0",
      }}
    >
      <div style={{ fontSize: 13, color: "#64748B" }}>{label}</div>
      <div
        style={{
          fontSize: 13,
          fontWeight: 800,
          color: highlight ? "#B45309" : "#0F172A",
          textAlign: "right",
        }}
      >
        {value}
      </div>
    </div>
  )
}

function PreviewPillRow({ label, value }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 10,
        alignItems: "center",
        padding: "8px 0",
        borderBottom: "1px solid #E2E8F0",
      }}
    >
      <div style={{ fontSize: 12, color: "#64748B" }}>{label}</div>
      <div
        style={{
          fontSize: 12,
          fontWeight: 900,
          color: "#0F172A",
          background: "#FFFFFF",
          border: "1px solid #E2E8F0",
          borderRadius: 999,
          padding: "5px 10px",
        }}
      >
        {value}
      </div>
    </div>
  )
}

function ComparisonRow({ row }) {
  const tone =
    row.delta === "up" ? "#166534" : row.delta === "down" ? "#B42318" : "#475569"

  const badge =
    row.delta === "up" ? "Mejora" : row.delta === "down" ? "Reduce" : "Igual"

  const badgeBg =
    row.delta === "up" ? "#ECFDF3" : row.delta === "down" ? "#FEF3F2" : "#F8FAFC"

  const badgeBorder =
    row.delta === "up" ? "#A6F4C5" : row.delta === "down" ? "#FECDCA" : "#E2E8F0"

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1.2fr 0.9fr 0.9fr 110px",
        borderBottom: "1px solid #E2E8F0",
        fontSize: 13,
      }}
    >
      <div style={{ padding: 12, color: "#334155", fontWeight: 700 }}>{row.label}</div>
      <div style={{ padding: 12, color: "#0F172A" }}>{row.current}</div>
      <div style={{ padding: 12, color: "#0F172A", fontWeight: 800 }}>{row.next}</div>
      <div style={{ padding: 12 }}>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            padding: "5px 10px",
            borderRadius: 999,
            background: badgeBg,
            border: `1px solid ${badgeBorder}`,
            color: tone,
            fontSize: 11,
            fontWeight: 900,
          }}
        >
          {badge}
        </span>
      </div>
    </div>
  )
}

function PriceImpactCard({
  title,
  currentLabel,
  currentValue,
  nextLabel,
  nextValue,
  deltaValue,
  cycleLabel,
  selectedBillingCycle,
  annualSavings,
  effectiveMonthlyEquivalent,
  annualMonthsSaved,
  emphasizeSavings,
}) {
  const deltaTone =
    deltaValue > 0 ? "#B45309" : deltaValue < 0 ? "#166534" : "#475569"

  const deltaText =
    deltaValue > 0
      ? `+ ${formatMoney(Math.abs(deltaValue))}`
      : deltaValue < 0
        ? `- ${formatMoney(Math.abs(deltaValue))}`
        : "Sin diferencia"

  const showAnnualSavings =
    selectedBillingCycle === "YEARLY" &&
    Number(annualSavings || 0) > 0 &&
    emphasizeSavings

  const roundedMonthsSaved =
    Number(annualMonthsSaved || 0) > 0
      ? Math.round(Number(annualMonthsSaved || 0) * 10) / 10
      : 0

  return (
    <div
      style={{
        background: "linear-gradient(180deg, #FFFFFF 0%, #FAFBFF 100%)",
        border: "1px solid #E5E7EB",
        borderRadius: 20,
        padding: 16,
      }}
    >
      <div
        style={{
          fontSize: 16,
          fontWeight: 900,
          color: "#0F172A",
          marginBottom: 10,
        }}
      >
        {title}
      </div>

      <div
        style={{
          display: "grid",
          gap: 10,
        }}
      >
        <PriceRow label={currentLabel} value={currentValue} />
        <PriceRow label={nextLabel} value={nextValue} strong />

        {showAnnualSavings ? (
          <>
            <PriceRow
              label="Equivalente mensual del anual"
              value={formatMoney(effectiveMonthlyEquivalent)}
              color="#1D4ED8"
            />

            <PriceRow
              label="Ahorro anual vs pagar mes a mes"
              value={formatMoney(annualSavings)}
              color="#166534"
              strong
            />

            <PriceRow
              label="Equivalente en meses ahorrados"
              value={`${roundedMonthsSaved} mes(es)`}
              color="#166534"
            />
          </>
        ) : (
          <PriceRow
            label={`Diferencia ${cycleLabel}`}
            value={deltaText}
            color={deltaTone}
          />
        )}
      </div>

      {showAnnualSavings ? (
        <div
          style={{
            marginTop: 14,
            background: "#ECFDF3",
            border: "1px solid #A6F4C5",
            borderRadius: 16,
            padding: 12,
            color: "#166534",
          }}
        >
          <div
            style={{
              fontSize: 12,
              fontWeight: 900,
              textTransform: "uppercase",
              marginBottom: 6,
            }}
          >
            Beneficio del pago anual
          </div>

          <div
            style={{
              fontSize: 13,
              lineHeight: 1.7,
            }}
          >
            Elegir el anual reduce tu costo mensual efectivo y te permite ahorrar más
            que pagando mes a mes durante todo el año.
          </div>
        </div>
      ) : null}
    </div>
  )
}

function PriceRow({ label, value, strong = false, color = "#0F172A" }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 10,
        padding: "10px 0",
        borderBottom: "1px solid #E2E8F0",
      }}
    >
      <div style={{ fontSize: 13, color: "#64748B" }}>{label}</div>
      <div
        style={{
          fontSize: 13,
          fontWeight: strong ? 900 : 800,
          color,
          textAlign: "right",
        }}
      >
        {value}
      </div>
    </div>
  )
}

function ChangeSummaryCard({ gained, lost }) {
  return (
    <div
      style={{
        background: "#FFFFFF",
        border: "1px solid #E5E7EB",
        borderRadius: 20,
        padding: 16,
      }}
    >
      <div
        style={{
          fontSize: 16,
          fontWeight: 900,
          color: "#0F172A",
          marginBottom: 10,
        }}
      >
        Resumen del cambio
      </div>

      <div
        style={{
          display: "grid",
          gap: 12,
        }}
      >
        <div
          style={{
            background: "#ECFDF3",
            border: "1px solid #A6F4C5",
            borderRadius: 16,
            padding: 12,
          }}
        >
          <div
            style={{
              fontSize: 12,
              fontWeight: 900,
              color: "#166534",
              marginBottom: 6,
              textTransform: "uppercase",
            }}
          >
            Lo que mejoras
          </div>

          {gained.length ? (
            <div style={{ display: "grid", gap: 6, color: "#166534", fontSize: 13 }}>
              {gained.map((item) => (
                <div key={item}>• {item}</div>
              ))}
            </div>
          ) : (
            <div style={{ color: "#166534", fontSize: 13 }}>
              No hay mejoras diferenciales en esta comparación.
            </div>
          )}
        </div>

        <div
          style={{
            background: "#FFF7ED",
            border: "1px solid #FDBA74",
            borderRadius: 16,
            padding: 12,
          }}
        >
          <div
            style={{
              fontSize: 12,
              fontWeight: 900,
              color: "#9A3412",
              marginBottom: 6,
              textTransform: "uppercase",
            }}
          >
            Lo que podrías perder
          </div>

          {lost.length ? (
            <div style={{ display: "grid", gap: 6, color: "#9A3412", fontSize: 13 }}>
              {lost.map((item) => (
                <div key={item}>• {item}</div>
              ))}
            </div>
          ) : (
            <div style={{ color: "#9A3412", fontSize: 13 }}>
              No perderías capacidades frente a tu plan actual.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}